import { RoutingConfig } from 'src/app/utils/routing-config';
import { Network } from '@capacitor/network';
import { ToastUtils } from './utils/toast_builder';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
  Component,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  OnInit,
} from '@angular/core';
import { handleResponse } from './utils/user_data_storage';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor, PluginListenerHandle, Plugins } from '@capacitor/core';
import { PopoverComponent } from './widgets/popover/popover.component';
import { AnimationUtils } from './utils/animations';
import { lastValueFrom, take } from 'rxjs';
import Owner from './models/owner.model';
import { Store } from '@ngrx/store';
import { register } from 'swiper/element/bundle';
import { Location } from '@angular/common';
import { ConnectionService } from './services/connection.service';
import {
  getOwnerById,
} from './utils/states-management/actions/owner.action';

const { App } = Plugins;
register();
import routes from './config/routing.json';
import {
  AlertController,
  IonTabs,
  Platform,
  PopoverController,
} from '@ionic/angular';
import { SwUpdate } from '@angular/service-worker';
import { LoginService } from './apis/login.service';
import { VersionCheckService } from './apis/version-check.service';
import { HttpClient } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { setUserCountry } from './utils/states-management/actions/countries.action';
import { ConnectionState } from './utils/states-management/reducers/offline.reducer';
import { Geolocation } from '@capacitor/geolocation';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  animations: [
    trigger('pulseAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0 }))
      ]),
      state('*', style({
        opacity: 1,
        filter: 'brightness(1.5)'
      }))
    ])
  ]
})
export class AppComponent implements OnDestroy, OnInit {
  @ViewChild('container', { read: ViewContainerRef })
  container: ViewContainerRef;
  @ViewChild(IonTabs) ionTabs: IonTabs;

  steps: { template: string; title: string }[];
  animation;
  selectedStep: number = 0;
  isSecondBackClicked = false;
  device_info;
  loadingModal;
  isHomeView;
  routing;
  showLandingHeader = false;
  isOnline = true;
  appSyncState = {
    isSyncDone: true,
    isSyncOnProgress: false,
    operation: {},
    isOnline: true,
    showOfflineStatus: false,
    connectionQuality: 'good' as 'good' | 'poor' | 'offline',
  };
  isMainAnalysisPage: boolean = true;

  showConnectionStatus = false;
  owner: Owner;
  devicePlatform = 'web';
  offlinePopOver;
  unSyncOperationLength = 0;
  updateReqPopOver = null;
  isAppVersionChecked = false;
  private appStateListener: PluginListenerHandle;
  private visibilityChangeListener: any;
  constructor(
    private popoverController: PopoverController,
    public router: Router,
    private platform: Platform,
    private toastUtils: ToastUtils,
    private animationUtils: AnimationUtils,
    private store: Store<{ owner: Owner; connection: ConnectionState }>,
    private translate: TranslateService,
    private routingConfig: RoutingConfig,
    private location: Location,
    private swUpdate: SwUpdate,
    private loginService: LoginService,
    private versionService: VersionCheckService,
    private alertCtrl: AlertController,
    private http: HttpClient,
    private connectionService: ConnectionService
  ) {
    // this.connectionService.checkConnectionQuality()
    const platformV = Capacitor.getPlatform();
    if (platformV === 'ios' || platformV === 'android') {
      this.devicePlatform = platformV
    } else {
      this.devicePlatform = 'web'
    }

    this.animationUtils.routerAnimation().then((value) => {
      this.animation = value;
    });

    this.router.events.subscribe(async (val: RouterEvent) => {
      this.isHomeView = this.router.url.includes(routes.dashboard.home);

      let routing = await this.routingConfig.getLandingRoutingConfig();
      this.showLandingHeader =
        routing?.find(
          (val) => val.route.includes(this.router.url) && val.tabIndex > -1
        ) &&
          !this.router.url.includes('home') &&
          !this.router.url.includes('login') &&
          !this.router.url.includes('landing_done') &&
          this.router.url != '/'
          ? true
          : false;
    });

    this.platform.backButton.subscribeWithPriority(9999, async (event: any) => {
      if (!this.isSecondBackClicked) {
        this.isSecondBackClicked = true;
        document.addEventListener(
          'backbutton',
          async function (event) {
            event.preventDefault();
          },
          true
        );
      } else {
        App.exitApp();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    console.log('AppComponent initialized');
    if (this.platform.is('ios') || this.platform.is('android')) {
      await this.checkMapPermissions();
    }
    this.store.select('connection').subscribe(async connection => {
      this.appSyncState.isOnline = connection.isOnline;
      this.appSyncState.isSyncOnProgress = connection.isSyncOnProgress;
      this.appSyncState.connectionQuality = connection.connectionQuality;
      this.appSyncState.showOfflineStatus = (connection.connectionQuality !== 'good' || connection.isSyncOnProgress == true);
      if (!this.appSyncState.isOnline &&  this.offlinePopOver) {
        await this.offlinePopOver.dismiss();

        await this.handleOfflinePopover(connection.connectionQuality);
      } else if (this.offlinePopOver) {
        await this.offlinePopOver.dismiss();
        this.offlinePopOver = null;
      }
    });

    if (this.appSyncState.isOnline && !this.isAppVersionChecked) {
      await this.versionService.checkAppVersion(true);
      this.isAppVersionChecked = true;
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(async (event: NavigationEnd) => {
      // Only check on main/important routes
      if (event.url.includes('/login') && !this.appSyncState.isOnline) {
        await this.handleOfflinePopover(this.appSyncState.connectionQuality);
      }

    });
    this.store.dispatch(getOwnerById());
    let config = await lastValueFrom(await this.routingConfig.getConfigObject())
    this.routing = config?.landing?.routingConfig;
    await this.navigate();

    // Subscribe to update notifications
    this.versionService.updateAvailable.subscribe(isAvailable => {
      if (isAvailable) {
        // this.updateSW();
      }
    });
  }

  async ngOnDestroy() {
    if (await navigator.serviceWorker.getRegistrations()) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    // Clean up version checking listeners
    Network.removeAllListeners();
    if (this.appStateListener) {
      this.appStateListener.remove();
    }
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }

  }

  async checkMapPermissions() {
    Geolocation.checkPermissions().then(async (result) => {
      console.log(result)

      if (result.location == 'granted') {
        // this.show_map_button = true;
      } else {
        const request = await Geolocation.requestPermissions();
        if (request.location == 'granted') {
          // this.show_map_button = true;
        } else if (request.location == 'prompt') {
          // this.show_map_button = false;
          setTimeout(() => {
            this.checkMapPermissions();
          }, 1000);
        } else {

          // this.show_map_button = false;
        }
      }
    });
  }
  async navigate() {
    this.platform.ready().then(async () => {
      this.store
        .select('owner')
        .pipe(take(1))
        .subscribe({
          next: async (owner: any) => {
            this.owner = owner;
            if (!owner?.country_id) {
              return;
            }
            this.routingConfig.getConfigObject();
            if (owner && owner?.checkpoint_path) {
              if (owner?.preferred_language) {
                this.translate.use(owner?.preferred_language);
              }
              if (owner?.countrie) {
                this.store.dispatch(setUserCountry({ country: owner?.countrie }))
              }
              if (owner?.checkpoint_path.includes('home')) {
                let profileConfig = await this.routingConfig.getProfileFormConfig();
                this.router.navigateByUrl('/home');
                await handleResponse(owner);
              } else if (owner?.checkpoint_path) {


                await this.router.navigateByUrl(owner.checkpoint_path);
                let componentProps = {
                  key1: await this.translate.instant('landing_creation_info'),
                  close_button: await this.translate.instant('submit'),
                };
                await this.toastUtils.popOverBuilder(
                  componentProps,
                  PopoverComponent,
                  'popover-custom'
                );
              }
            }
          },
        });
    });
  }
  syncPopover = null
  async showPopOver(isOfflineToast) {
    let componentProps
    if (isOfflineToast) {
      componentProps = {
        key1: await this.translate.instant('offline_notification'),
      };
    } else {
      componentProps = {
        key1: 'app_offline_info_sync',
        canBeClosed: true,
        is_confirm_needed: false,
        is_info_popover: false,
        is_loader_needed: true,
      }
    }
    if (!this.syncPopover) {

      this.syncPopover = await this.toastUtils.popOverBuilder(
        componentProps,
        PopoverComponent,
        'popover-custom'
      );
    } else {
      this.syncPopover.dismiss();
      this.syncPopover = null;
    }
  }

  handelTabClicked = (event: MouseEvent) => {
    const { tab } = event
      .composedPath()
      ?.find(
        (element: any) => element.tagName === 'ION-TAB-BUTTON'
      ) as EventTarget & { tab: string };

    if (
      [
        'home/wallet',
        'home/book',
        'home/analysis',
        'home/profile',
        'home/menu',
      ].includes(tab) &&
      this.ionTabs.outlet.canGoBack(1, tab)
    ) {
      event.stopImmediatePropagation();
      return this.ionTabs.outlet.pop(3, tab);
    }
  };


  /**
   * Handle offline popover display based on connection quality and app state
   */
  private async handleOfflinePopover(connectionQuality: 'good' | 'poor' | 'offline'): Promise<void> {
    const owner = await lastValueFrom(this.store.select('owner').pipe(take(1)));
    await this.offlinePopOver.dismiss();
    if (connectionQuality !== 'offline' && this.offlinePopOver) {
      await this.offlinePopOver.dismiss();
      this.offlinePopOver = null;
      return;
    }
    if (connectionQuality !== 'offline' || this.offlinePopOver) {
      return;
    }
    const shouldShowPopover = (owner?.id && !owner?.checkpoint_path?.includes('home')) ||
      this.router.url.includes('login');

    if (shouldShowPopover) {
      const CProps = {
        key1: this.router.url.includes('login')
          ? await this.translate.instant('app_offline_info_login')
          : await this.translate.instant('app_offline_info'),
        canBeClosed: false,
        is_confirm_needed: false,
      };

      this.offlinePopOver = await this.popoverController.create({
        component: PopoverComponent,
        cssClass: 'popover-custom',
        animated: true,
        componentProps: CProps,
        backdropDismiss: false,
      });

      await this.offlinePopOver.present();
    }
  }


}


